import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Spin } from "antd";
import {
  CalendarOutlined,
  MessageOutlined,
  UserAddOutlined,
  DollarCircleOutlined,
  RightOutlined,
  SmileOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { PATH } from "../../redux/utils";
import { getEmpresaConfig } from "../../components/utils/empresaConfig";

dayjs.locale("es");

const money = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", { style: "currency", currency: "MXN" });

// Panel de inicio de Visum: combina Agenda + KPIs financieros de Cargos y
// Pagos (Caso C de panel-inicio-adaptable - no hay un modulo "Ventas"
// clasico en esta clinica, Cargos y Pagos es el analogo financiero real).
function Home() {
  const navigate = useNavigate();
  const today = useMemo(() => dayjs(), []);
  const [empresa, setEmpresa] = useState({ nombre: "" });
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchResumen = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${PATH}/dashboard/resumen`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResumen(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getEmpresaConfig().then((c) => setEmpresa(c || {}));
    fetchResumen();
  }, [fetchResumen]);

  const kpis = resumen?.kpis || {};
  const operacion = resumen?.operacion || {};
  const agendaHoy = resumen?.agenda_hoy || [];
  const mensajesPendientes = resumen?.alertas?.mensajes_pendientes || [];

  const subtitulo =
    operacion.citas_semana > 0
      ? `Tienes ${operacion.citas_semana} cita${operacion.citas_semana === 1 ? "" : "s"} programada${operacion.citas_semana === 1 ? "" : "s"} esta semana.`
      : "No tienes citas programadas esta semana.";

  return (
    <>
      <main className="hm-main">
        <div className="hm-content">
          {/* ── BIENVENIDA ─────────────────────────────── */}
          <section className="hm-welcome">
            <div className="hm-welcome-left">
              {empresa.nombre && <div className="hm-eyebrow">{empresa.nombre}</div>}
              <div className="hm-greeting">Panel de Inicio</div>
              <div className="hm-date">{today.format("dddd, D [de] MMMM [de] YYYY")}</div>
              {!loading && <div className="hm-subtitle">{subtitulo}</div>}
            </div>
            <button type="button" className="hm-btn-primary" onClick={() => navigate("/app/agenda")}>
              <CalendarOutlined /> Abrir agenda
            </button>
          </section>

          {loading ? (
            <div className="hm-card">
              <div className="hm-center">
                <Spin size="large" />
              </div>
            </div>
          ) : (
            <>
              {/* ── KPIs DEL MES ─────────────────────────── */}
              <section className="hm-kpi-row">
                <div className="hm-kpi-card">
                  <div className="hm-kpi-label">Cobrado este mes</div>
                  <div className="hm-kpi-value">{money(kpis.total_cobrado_mes)}</div>
                </div>
                <div className="hm-kpi-card">
                  <div className="hm-kpi-label">Saldo pendiente</div>
                  <div className="hm-kpi-value">{money(kpis.total_pendiente)}</div>
                </div>
                <div className="hm-kpi-card">
                  <div className="hm-kpi-label">Cargos abiertos</div>
                  <div className="hm-kpi-value">{kpis.cargos_abiertos ?? 0}</div>
                </div>
                <div className="hm-kpi-card">
                  <div className="hm-kpi-label">Pacientes nuevos (mes)</div>
                  <div className="hm-kpi-value">{kpis.pacientes_nuevos_mes ?? 0}</div>
                </div>
              </section>

              {/* ── ESTADO OPERATIVO ─────────────────────── */}
              <section className="hm-op-row">
                <div className="hm-op-card">
                  <CalendarOutlined className="hm-op-icon hm-op-icon--citas" />
                  <div className="hm-op-body">
                    <div className="hm-op-value">{operacion.citas_hoy ?? 0}</div>
                    <div className="hm-op-label">Citas de hoy</div>
                  </div>
                </div>
                <div className="hm-op-card">
                  <ClockCircleOutlined className="hm-op-icon hm-op-icon--semana" />
                  <div className="hm-op-body">
                    <div className="hm-op-value">{operacion.citas_semana ?? 0}</div>
                    <div className="hm-op-label">Citas esta semana</div>
                  </div>
                </div>
                <div className="hm-op-card">
                  <MessageOutlined className="hm-op-icon hm-op-icon--mensajes" />
                  <div className="hm-op-body">
                    <div className="hm-op-value">{operacion.mensajes_pendientes ?? 0}</div>
                    <div className="hm-op-label">Mensajes sin responder</div>
                  </div>
                </div>
              </section>

              {/* ── AGENDA DE HOY + ALERTAS ──────────────── */}
              <section className="hm-alerts-row">
                <div className="hm-card hm-alert-card">
                  <div className="hm-card-header">
                    <div className="hm-card-title">
                      <CalendarOutlined style={{ color: "var(--brand-primary)" }} />
                      Citas de hoy
                    </div>
                    {agendaHoy.length > 0 && (
                      <span className="hm-card-badge">{agendaHoy.length}</span>
                    )}
                  </div>
                  {agendaHoy.length > 0 ? (
                    <div className="hm-alert-list">
                      {agendaHoy.map((c) => (
                        <button
                          key={c.id_cita}
                          type="button"
                          className="hm-alert-row"
                          onClick={() => navigate("/app/agenda")}
                        >
                          <span className="hm-alert-name">{dayjs(c.fecha_hora_inicio).format("HH:mm")} — {c.paciente_nombre || "Paciente"}</span>
                          <span className="hm-alert-meta">{c.tipo_cita} · {c.estado}</span>
                          <RightOutlined className="hm-alert-arrow" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="hm-empty hm-empty--small">
                      <SmileOutlined className="hm-empty-icon" />
                      <span>Sin citas programadas para hoy</span>
                    </div>
                  )}
                </div>

                <div className="hm-card hm-alert-card">
                  <div className="hm-card-header">
                    <div className="hm-card-title">
                      <MessageOutlined style={{ color: "#d97706" }} />
                      Mensajes de contacto pendientes
                    </div>
                    {mensajesPendientes.length > 0 && (
                      <span className="hm-card-badge hm-card-badge--warn">{mensajesPendientes.length}</span>
                    )}
                  </div>
                  {mensajesPendientes.length > 0 ? (
                    <div className="hm-alert-list">
                      {mensajesPendientes.map((m) => (
                        <button
                          key={m.id_mensaje}
                          type="button"
                          className="hm-alert-row"
                          onClick={() => navigate("/app/mensajes")}
                        >
                          <span className="hm-alert-name">{m.nombre_contacto}</span>
                          <span className="hm-alert-meta">{dayjs(m.fecha).format("DD/MM/YYYY")}</span>
                          <RightOutlined className="hm-alert-arrow" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="hm-empty hm-empty--small">
                      <SmileOutlined className="hm-empty-icon" />
                      <span>Sin mensajes pendientes</span>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </main>

      <style>{CSS}</style>
    </>
  );
}

const CSS = `
.hm-main {
  background: #e7e7e7;
  min-height: calc(100vh - 44px);
  padding: 28px 20px;
  display: flex;
  flex-direction: column;
}
.hm-content { width: 100%; display: flex; flex-direction: column; gap: 24px; flex: 1; min-height: 0; }

.hm-welcome {
  display: flex; align-items: flex-end; justify-content: space-between; gap: 16px;
  background: #fff; border-radius: 18px; padding: 32px 36px;
  box-shadow: 0 3px 14px rgba(15,34,58,0.07); border: 1px solid #e4e8f0;
}
.hm-eyebrow { font-size: 12px; font-weight: 700; color: var(--brand-primary); text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 4px; }
.hm-greeting { font-size: 30px; font-weight: 700; color: #0d1b4b; line-height: 1.2; letter-spacing: -0.3px; }
.hm-date { font-size: 13px; color: var(--brand-primary); margin-top: 4px; text-transform: capitalize; }
.hm-subtitle { font-size: 13.5px; color: #7a7f87; margin-top: 8px; }
.hm-btn-primary {
  display: flex; align-items: center; gap: 8px; background: var(--brand-primary); color: #fff;
  border: none; border-radius: 12px; padding: 10px 18px; font-weight: 700; font-size: 13.5px;
  cursor: pointer; white-space: nowrap; transition: background 0.15s;
}
.hm-btn-primary:hover { background: var(--brand-primary-hover, var(--brand-secondary)); }

.hm-card {
  background: #fff; border-radius: 18px; border: 1px solid #e4e8f0;
  box-shadow: 0 3px 14px rgba(15,34,58,0.07); overflow: hidden;
  display: flex; flex-direction: column;
}
.hm-card-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px 12px; border-bottom: 1px solid #f0f2f6; }
.hm-card-title { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 15px; color: #0d1b4b; }
.hm-card-badge { font-size: 12px; font-weight: 700; color: #7a7f87; background: #f3f5f8; padding: 2px 10px; border-radius: 999px; }
.hm-card-badge--warn { color: #b45309; background: #fffbeb; }

.hm-kpi-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
.hm-kpi-card {
  background: #fff; border-radius: 16px; border: 1px solid #e4e8f0;
  box-shadow: 0 3px 14px rgba(15,34,58,0.06); padding: 18px 20px;
  display: flex; flex-direction: column; gap: 8px;
}
.hm-kpi-label { font-size: 12.5px; font-weight: 600; color: #7a7f87; }
.hm-kpi-value { font-size: 24px; font-weight: 800; color: #0d1b4b; }

.hm-op-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
.hm-op-card {
  background: #fff; border-radius: 16px; border: 1px solid #e4e8f0;
  box-shadow: 0 3px 14px rgba(15,34,58,0.06); padding: 18px 20px;
  display: flex; align-items: center; gap: 14px;
}
.hm-op-icon { font-size: 26px; flex-shrink: 0; }
.hm-op-icon--citas { color: var(--brand-primary); }
.hm-op-icon--semana { color: var(--brand-secondary); }
.hm-op-icon--mensajes { color: #d97706; }
.hm-op-value { font-size: 22px; font-weight: 800; color: #0d1b4b; line-height: 1.1; }
.hm-op-label { font-size: 12.5px; color: #7a7f87; font-weight: 600; }

.hm-alerts-row { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.hm-alert-card { min-height: 220px; }
.hm-alert-list { display: flex; flex-direction: column; overflow-y: auto; max-height: 260px; }
.hm-alert-row {
  display: flex; align-items: center; gap: 10px; padding: 11px 20px; border: none;
  background: transparent; cursor: pointer; text-align: left; width: 100%;
  border-bottom: 1px solid #f5f6f8; transition: background 0.13s;
}
.hm-alert-row:last-child { border-bottom: none; }
.hm-alert-row:hover { background: #f7f9fc; }
.hm-alert-name { font-size: 13px; font-weight: 600; color: #0d1b4b; flex-shrink: 0; max-width: 55%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hm-alert-meta { font-size: 12px; color: #8a93a2; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hm-alert-arrow { color: #c4cad6; font-size: 11px; flex-shrink: 0; }

.hm-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 36px 20px; color: #b0b8c6; font-size: 13px; flex: 1; }
.hm-empty--small { padding: 24px 20px; }
.hm-empty-icon { font-size: 28px; color: #d0d8e6; }
.hm-center { display: flex; align-items: center; justify-content: center; padding: 60px; flex: 1; }

@media (max-width: 1100px) {
  .hm-kpi-row { grid-template-columns: repeat(2, 1fr); }
  .hm-op-row { grid-template-columns: 1fr; }
}
@media (max-width: 860px) {
  .hm-alerts-row { grid-template-columns: 1fr; }
}
@media (max-width: 600px) {
  .hm-main { padding: 16px; }
  .hm-welcome { padding: 24px 20px; flex-direction: column; align-items: flex-start; }
  .hm-greeting { font-size: 22px; }
  .hm-kpi-row { grid-template-columns: 1fr; }
}
`;

export default Home;
